import { TransactionResponse, formatEther } from 'ethers';
import { Multicall__factory, NLAAM__factory } from 'public/abi/evm/types';
import useProvider from '~/composables/useProvider';
import type { ContractCallParams } from '~/types/Web3Provider';
import { useWalletInfo, useWeb3ModalAccount } from '@web3modal/ethers/vue';
import useSendContractMethod from '~/composables/useSendContractMethod';
import {
  getCancelErrorMessage,
  modifyHistoryHashStatus,
  saveHashToLocalStorage,
  useEnvs,
  useMainStore
} from '#imports';
import { BigNumber } from 'bignumber.js';
import type { BlockchainConfig } from '~/types/contractAddresses';
import { shallowRef, triggerRef } from 'vue';

// Configuration for pirate market tokens
export interface PirateMarketTokenConfig {
  tokenKey: string;
  tokenIndex: number;
}

// Get the token address from the contract config
const getTokenAddress = (contracts: BlockchainConfig['contracts'], tokenKey: string): string | null => {
  const contract = contracts[tokenKey as keyof typeof contracts];
  if (typeof contract === 'string') {
    return contract;
  }
  if (contract && typeof contract === 'object' && 'addresses' in contract) {
    return contract.addresses.contract || null;
  }
  return null;
};

// Configuration for all pirate market tokens
export const PIRATE_MARKET_TOKENS_CONFIG: PirateMarketTokenConfig[] = [
  { tokenKey: 'pearl', tokenIndex: 0 },
  // { tokenKey: 'blackPearl', tokenIndex: 0 },
  { tokenKey: 'coal', tokenIndex: 0 },
  { tokenKey: 'goldOre', tokenIndex: 0 },
  { tokenKey: 'wood', tokenIndex: 0 },
  { tokenKey: 'ironOre', tokenIndex: 0 },
  { tokenKey: 'sand', tokenIndex: 0 },
  { tokenKey: 'clay', tokenIndex: 0 },
  { tokenKey: 'acorn', tokenIndex: 0 },
  { tokenKey: 'leaf', tokenIndex: 0 },
  { tokenKey: 'gold', tokenIndex: 0 },
  { tokenKey: 'woodenPlank', tokenIndex: 0 },
  { tokenKey: 'iron', tokenIndex: 0 },
  { tokenKey: 'hammer', tokenIndex: 0 },
  { tokenKey: 'nail', tokenIndex: 0 },
  { tokenKey: 'heatinglog', tokenIndex: 0 },
  { tokenKey: 'pickaxe', tokenIndex: 0 },
  { tokenKey: 'shovel', tokenIndex: 0 },
  { tokenKey: 'saw', tokenIndex: 0 },
  { tokenKey: 'bed', tokenIndex: 0 },
  { tokenKey: 'table', tokenIndex: 0 },
  { tokenKey: 'broom', tokenIndex: 0 },
  { tokenKey: 'metalJoin', tokenIndex: 0 },
  { tokenKey: 'glass', tokenIndex: 0 },
  { tokenKey: 'brick', tokenIndex: 0 },
  { tokenKey: 'roofTile', tokenIndex: 0 },
  { tokenKey: 'floorTile', tokenIndex: 0 },
  { tokenKey: 'bottle', tokenIndex: 0 },
  { tokenKey: 'cup', tokenIndex: 0 },
  { tokenKey: 'axe', tokenIndex: 0 },
  { tokenKey: 'rope', tokenIndex: 0 },
  { tokenKey: 'torch', tokenIndex: 0 },
  { tokenKey: 'potion', tokenIndex: 0 },
  { tokenKey: 'antidote', tokenIndex: 0 },
  { tokenKey: 'resin', tokenIndex: 0 },
  { tokenKey: 'mushroom', tokenIndex: 0 },
  { tokenKey: 'root', tokenIndex: 0 },
  { tokenKey: 'flower', tokenIndex: 0 },
  { tokenKey: 'redWood', tokenIndex: 0 },
  { tokenKey: 'mineral', tokenIndex: 0 },
  { tokenKey: 'bat', tokenIndex: 0 },
  { tokenKey: 'deadWater', tokenIndex: 0 },
  { tokenKey: 'bone', tokenIndex: 0 },
  // NFT tokens with multiple indices
  { tokenKey: 'woodenHuts', tokenIndex: 0 },
  { tokenKey: 'woodenHuts', tokenIndex: 1 },
  { tokenKey: 'woodenHuts', tokenIndex: 2 },
  { tokenKey: 'woodenHuts', tokenIndex: 3 },
  { tokenKey: 'woodenHuts', tokenIndex: 4 },
  { tokenKey: 'brickHuts', tokenIndex: 0 },
  { tokenKey: 'brickHuts', tokenIndex: 1 },
  { tokenKey: 'brickHuts', tokenIndex: 2 },
  { tokenKey: 'brickHuts', tokenIndex: 3 },
  { tokenKey: 'brickHuts', tokenIndex: 4 },
  { tokenKey: 'tiledBrickHuts', tokenIndex: 0 },
  { tokenKey: 'tiledBrickHuts', tokenIndex: 1 },
  { tokenKey: 'tiledBrickHuts', tokenIndex: 2 },
  { tokenKey: 'tiledBrickHuts', tokenIndex: 3 },
  { tokenKey: 'tiledBrickHuts', tokenIndex: 4 }
];

export interface PirateMarketItem {
  tokenAddress: string;
  tokenKey: string;
  tokenIndex: number;
  price: number;
  rawPrice: string;
  basePrice: number;
  targetPrice: number;
}

interface IUsePiratesMarket {
  piratesMarketData: Ref<PirateMarketItem[]>;
  isLoading: Ref<boolean>;
  hasInitiallyLoaded: Ref<boolean>;
  error: Ref<string | null>;
  fetchPrices: () => Promise<void>;
  buyFromPiratesMarket: (
    tokenAddress: string,
    tokenIndex: number,
    amount: number,
    rawPrice: string
  ) => Promise<TransactionResponse | undefined>;
  isButtonLoading: Ref<boolean>;
  showInsufficientFundsDialog: Ref<boolean>;
  showErrorDialog: Ref<boolean>;
  showSuccessDialog: Ref<boolean>;
  linkToTransaction: Ref<string>;
  countTokensPurchased: Ref<number>;
  transactionPrice: Ref<number>;
  errorMessage: Ref<string>;
}

const POLLING_INTERVAL = 600000; // 10 minutes

export default async (): Promise<IUsePiratesMarket> => {
  const { t } = useI18n();
  const { address } = useWeb3ModalAccount();
  const { walletInfo } = useWalletInfo();
  const { sendContractMethod } = useSendContractMethod();
  const { blockchain } = useEnvs();
  const store = useMainStore();
  const { getProvider, getSigner } = useProvider();

  const { allowanceNlaam } = useTokensReader();
  const { approveSpendUsdt } = useStakingMethods();
  const usdtContract = ref(blockchain.contracts.usdt);
  const { balance } = await useBalance(usdtContract);

  // Use shallowRef to prevent deep reactivity - we'll manually trigger updates
  const piratesMarketData = shallowRef<PirateMarketItem[]>([]);
  const isLoading = ref(false);
  const hasInitiallyLoaded = ref(false);
  const error = ref<string | null>(null);

  const isButtonLoading = ref(false);
  const showInsufficientFundsDialog = ref(false);
  const showErrorDialog = ref(false);
  const showSuccessDialog = ref(false);
  const errorMessage = ref('');
  const linkToTransaction = ref('');
  const countTokensPurchased = ref(0);
  const transactionPrice = ref(0);

  let pollingInterval: ReturnType<typeof setInterval> | null = null;

  const sendContractMethodExtended = async <T extends Record<any, any>, M extends keyof T>(
    sendParams: ContractCallParams<T, M>,
    onConfirmation?: (confirmations?: number) => void
  ) => {
    const from = address.value;
    return sendContractMethod({ ...sendParams, from }, onConfirmation);
  };

  const fetchPrices = async () => {
    // Check if NLAAM contract is configured
    if (!blockchain.contracts.nlaam || blockchain.contracts.nlaam === '0x0000000000000000000000000000000000000000') {
      console.log('[PiratesMarket] NLAAM contract not configured');
      piratesMarketData.value = [];
      hasInitiallyLoaded.value = true;
      return;
    }

    // Only show loading state on initial fetch (before first successful load)
    const isInitialFetch = !hasInitiallyLoaded.value;

    try {
      if (isInitialFetch) {
        isLoading.value = true;
      }
      error.value = null;

      const multicallContract = Multicall__factory.connect(blockchain.multicallAddress, getProvider());
      const nlaamInterface = NLAAM__factory.createInterface();

      // Build calldata for each token
      const validTokens: { config: PirateMarketTokenConfig; tokenAddress: string }[] = [];
      const callData: [string, string][] = [];

      for (const config of PIRATE_MARKET_TOKENS_CONFIG) {
        const tokenAddress = getTokenAddress(blockchain.contracts, config.tokenKey);
        if (!tokenAddress) {
          console.log(`[PiratesMarket] Token ${config.tokenKey} has no address configured`);
          continue;
        }

        validTokens.push({ config, tokenAddress });
        const encodedCall = nlaamInterface.encodeFunctionData('getPrices', [tokenAddress, config.tokenIndex]);
        callData.push([blockchain.contracts.nlaam, encodedCall]);
      }

      if (callData.length === 0) {
        console.log('[PiratesMarket] No valid tokens found to query');
        piratesMarketData.value = [];
        return;
      }

      console.log(
        `[PiratesMarket] Querying prices for ${callData.length} tokens from NLAAM at ${blockchain.contracts.nlaam}`
      );

      const multicallResult = await multicallContract?.tryAggregate.staticCall(
        false, // requireSuccess = false - allows individual calls to fail without reverting
        callData.map((item) => ({
          target: item[0],
          callData: item[1]
        }))
      );

      // Build a map of new prices for quick lookup
      const newPricesMap = new Map<
        string,
        { price: number; rawPrice: string; basePrice: number; targetPrice: number }
      >();
      const newTokens: PirateMarketItem[] = [];
      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < multicallResult.length; i++) {
        const result = multicallResult[i];
        const { config, tokenAddress } = validTokens[i];

        // Check if call succeeded (result[0] is success boolean)
        if (!result[0]) {
          failCount++;
          continue;
        }

        try {
          const decoded = nlaamInterface.decodeFunctionResult('getPrices', result[1]);
          // getPrices returns: basePrice, targetPrice, currentPrice
          const rawCurrentPrice = formatEther(decoded.currentPrice);
          const basePrice = Number(formatEther(decoded.basePrice));
          const targetPrice = Number(formatEther(decoded.targetPrice));
          const currentPrice = Number(rawCurrentPrice);

          successCount++;

          // Add all tokens that are configured in the contract (even if price is 0, it means it's configured)
          // Only skip if all prices are 0 (not configured)
          if (basePrice > 0 || targetPrice > 0 || currentPrice > 0) {
            const key = `${tokenAddress}-${config.tokenIndex}`;
            newPricesMap.set(key, { price: currentPrice, rawPrice: rawCurrentPrice, basePrice, targetPrice });
            newTokens.push({
              tokenAddress,
              tokenKey: config.tokenKey,
              tokenIndex: config.tokenIndex,
              price: currentPrice,
              rawPrice: rawCurrentPrice,
              basePrice,
              targetPrice
            });
          }
        } catch (decodeError) {
          failCount++;
          console.warn(
            `[PiratesMarket] Failed to decode price for ${config.tokenKey}[${config.tokenIndex}]:`,
            decodeError
          );
        }
      }

      console.log(
        `[PiratesMarket] Query complete: ${successCount} success, ${failCount} failed, ${newTokens.length} tokens with prices`
      );

      // If initial fetch or token list changed, replace the array
      if (isInitialFetch || piratesMarketData.value.length !== newTokens.length) {
        piratesMarketData.value = newTokens;
      } else {
        // Update prices in-place without triggering full re-render
        // With shallowRef, modifying item properties doesn't trigger reactivity
        for (const item of piratesMarketData.value) {
          const key = `${item.tokenAddress}-${item.tokenIndex}`;
          const newPrices = newPricesMap.get(key);
          if (newPrices) {
            item.price = newPrices.price;
            item.rawPrice = newPrices.rawPrice;
            item.basePrice = newPrices.basePrice;
            item.targetPrice = newPrices.targetPrice;
          }
        }
        // Manually trigger reactivity update for the shallowRef
        // This will update the UI without recreating components
        triggerRef(piratesMarketData);
      }

      // Mark as initially loaded after first successful fetch
      hasInitiallyLoaded.value = true;
    } catch (err) {
      console.error('[PiratesMarket] Error fetching prices:', err);
      error.value = err instanceof Error ? err.message : 'Unknown error';
      // Don't clear data on error during price updates, only on initial fetch
      if (isInitialFetch) {
        piratesMarketData.value = [];
      }
    } finally {
      if (isInitialFetch) {
        isLoading.value = false;
      }
    }
  };

  const buyNlaamContract = async (tokenAddress: string, tokenIndex: number, amount: number) => {
    return sendContractMethodExtended({
      contract: NLAAM__factory.connect(blockchain.contracts.nlaam, await getSigner()),
      methodName: 'buy',
      args: [tokenAddress, tokenIndex, amount]
    });
  };

  const buyFromPiratesMarket = async (
    tokenAddress: string,
    tokenIndex: number,
    amount: number,
    rawPrice: string
  ): Promise<TransactionResponse | undefined> => {
    try {
      if (!balance.value || !address.value) {
        return;
      }

      isButtonLoading.value = true;

      const totalCost = new BigNumber(rawPrice).times(amount);
      const totalCostFixed = totalCost.toFixed(18);
      const isEnoughBalance = new BigNumber(balance.value).gte(totalCost);

      if (!isEnoughBalance) {
        transactionPrice.value = totalCost.toNumber();
        showInsufficientFundsDialog.value = true;
        return;
      }

      // Check and handle approval
      const allowedCount = await allowanceNlaam(address.value);
      const shouldCallApprove = new BigNumber(allowedCount).isLessThan(totalCost);

      if (shouldCallApprove) {
        const transactionApprove = await approveSpendUsdt(totalCostFixed, blockchain.contracts.nlaam);

        if (!transactionApprove?.hash) {
          return;
        }

        saveHashToLocalStorage(t('notificationApprove'), transactionApprove.hash);
        store.updateVersion();

        await transactionApprove?.wait();
      }

      // Execute buy transaction
      const transactionBuy: TransactionResponse | undefined = await buyNlaamContract(tokenAddress, tokenIndex, amount);

      if (!transactionBuy?.hash) {
        return;
      }

      const token = tokenMetaData(blockchain.contracts, tokenAddress);

      saveHashToLocalStorage(
        `${t('notificationBuy')} (${amount} ${token?.methodName?.toUpperCase() || 'TOKEN'})`,
        transactionBuy.hash
      );

      store.updateVersion();

      await transactionBuy.wait();

      modifyHistoryHashStatus(transactionBuy?.hash, 'Done');

      store.updateVersion();
      store.updateBuypackCounter();

      countTokensPurchased.value = amount;
      transactionPrice.value = totalCost.toNumber();

      showSuccessDialog.value = true;
      linkToTransaction.value = blockchain.blockScanUrl + '/tx/' + transactionBuy?.hash;

      // Refresh prices after successful purchase
      await fetchPrices();

      return transactionBuy;
    } catch (err: unknown) {
      const currentProviderKind = walletInfo.value?.name;

      if (currentProviderKind) {
        errorMessage.value = getCancelErrorMessage(err, currentProviderKind);
      }

      showErrorDialog.value = true;

      console.error(err);
    } finally {
      isButtonLoading.value = false;
    }
  };

  // Start polling for prices
  const startPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    // Initial fetch
    fetchPrices();

    // Set up polling
    pollingInterval = setInterval(() => {
      fetchPrices();
    }, POLLING_INTERVAL);
  };

  // Stop polling
  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  };

  // Start polling immediately (don't rely on onMounted for async composables)
  startPolling();

  // Try to stop polling on unmount if lifecycle is available
  if (getCurrentInstance()) {
    onUnmounted(() => {
      stopPolling();
    });
  }

  return {
    piratesMarketData,
    isLoading,
    hasInitiallyLoaded,
    error,
    fetchPrices,
    buyFromPiratesMarket,
    isButtonLoading,
    showInsufficientFundsDialog,
    showErrorDialog,
    showSuccessDialog,
    linkToTransaction,
    countTokensPurchased,
    transactionPrice,
    errorMessage
  };
};
